import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import MainLayout from "layouts/MainLayout"
import Masonry from "react-masonry-css";
import { lenis } from "App"; 

import { getAlbumPhotos } from "api/api";

const AlbumDisplay = () => {
    const { albumName } = useParams();
    const [photos, setPhotos] = useState([]);
    const [albumTitle, setAlbumTitle] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState(null);
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const totalPages = Math.ceil(photos.length / itemsPerPage);
    const paginatedPhotos = photos.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const goNext = () => {
        setCurrentPage((prev) => {
            lenis.current.scrollTo(0, { immediate: false }); // force scroll to top
            const next = Math.min(prev + 1, totalPages);
            return next;
        });
    };

    const goPrev = () => {
        setCurrentPage((prev) => {
            lenis.current.scrollTo(0, { immediate: false }); // force scroll to top
            const prevPage = Math.max(prev - 1, 1);
            return prevPage;
        });
    };


    // Fetch photoss detailss
    useEffect(() => {
        const fetchAlbumPhotos = async () => {
            try {
                const data = await getAlbumPhotos(albumName);

                // Log to debug the structure
                // console.log("Fetched project data:", data.data.photos);

                // Set project safely
                if (data.data) {
                    setAlbumTitle(data.data.Album_Title);
                    setPhotos(data.data.photos);
                    console.log(data.data);
                }
            } catch (error) {
                console.error(error);
                navigate("/404", { replace: true });
                setErrors('Failed to fetch photos');
            } finally {
                setLoading(false);
            }
        };
        fetchAlbumPhotos();
    }, [setPhotos, albumName, navigate]);

    //Masonry Logic
    const breakpointColumnsObj = {
        default: 2,  // desktop and up
        768: 1,      // tablets
        0: 1         // phones
    };



    // The Password Protection Logic
    const [password, setPassword] = useState("");
    const [unlocked, setUnlocked] = useState(false);
    const [error, setError] = useState(false);

    const CORRECT_PASSWORD = "maybesahir";
    const STORAGE_KEY = "gallery_access";

    useEffect(() => {
        const savedAccess = localStorage.getItem(STORAGE_KEY);
        if (savedAccess) {
            const { timestamp } = JSON.parse(savedAccess);
            const now = Date.now();
            const threeHours = 3 * 60 * 60 * 1000;

            if (now - timestamp < threeHours) {
                setUnlocked(true);
            } else {
                localStorage.removeItem(STORAGE_KEY); // expired
            }
        }
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (password === CORRECT_PASSWORD) {
            setUnlocked(true);
            setError(false);
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ timestamp: Date.now() })
            );
        } else {
            setError(true);
            setPassword(""); // clear input
        }
    };

    if (!unlocked) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
                <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-sm text-center">
                    <h2 className="text-2xl font-semibold mb-4">Enter Password</h2>
                    <form onSubmit={handleSubmit}>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            className={`border rounded-lg px-4 py-2 w-full focus:ring-2 focus:outline-none transition ${error
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-300 focus:ring-black"
                                }`}
                        />
                        {error && (
                            <p className="text-red-500 text-sm mt-2">
                                Incorrect password. Try again.
                            </p>
                        )}
                        <button
                            type="submit"
                            className="bg-black text-white w-full py-2 mt-4 rounded-lg hover:bg-gray-800 transition"
                        >
                            Unlock
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <MainLayout>
            {loading && <p>Thinking...</p>}
            {errors && <p style={{ color: "red" }}>{errors}</p>}

            {!loading && !errors && (
                <div className="min-h-screen flex flex-col">
                    <section className="py-12 px-4 mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                            {albumTitle}
                        </h2>

                        {/* Pagination controls */}
                        <div className="flex justify-center mb-6 space-x-4">
                            <button onClick={goPrev} disabled={currentPage === 1} className="px-4 py-2 bg-gray-300 rounded">
                                Previous
                            </button>
                            <span className="px-2 py-2">{currentPage} / {totalPages}</span>
                            <button onClick={goNext} disabled={currentPage === totalPages} className="px-4 py-2 bg-gray-300 rounded">
                                Next
                            </button>
                        </div>

                        {/* Masonry Layout */}
                        <Masonry
                            breakpointCols={breakpointColumnsObj}
                            className="flex gap-4"
                            columnClassName="flex flex-col gap-4"
                        >
                            {paginatedPhotos.map((photo, index) => (
                                <div key={index} className="overflow-hidden shadow-lg break-inside-avoid">
                                    <img
                                        src={`${process.env.REACT_APP_ZVB_URL}assets/${photo.directus_files_id}`}
                                        alt={photo.alt}
                                        className="w-full h-auto object-cover transition-transform duration-500 hover:scale-105"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </Masonry>

                        {/* Pagination controls */}
                        <div className="flex justify-center mt-6 space-x-4">
                            <button onClick={goPrev} disabled={currentPage === 1} className="px-4 py-2 bg-gray-300 rounded">
                                Previous
                            </button>
                            <span className="px-2 py-2">{currentPage} / {totalPages}</span>
                            <button onClick={goNext} disabled={currentPage === totalPages} className="px-4 py-2 bg-gray-300 rounded">
                                Next
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </MainLayout>
    );
}

export default AlbumDisplay;